import { DocumentNode, print } from "graphql";

// Centralized GraphQL fetch helper for Hasura
// Provides environment-based URL selection, typed response, and consistent error handling.

export interface GraphqlRequestOptions<V = Record<string, any>> {
  query: string | DocumentNode;
  variables?: V; // Variables object
  headers?: Record<string, string>; // Extra headers (merged / can override)
  token?: string | null; // Explicit bearer token; falls back to localStorage when absent
}

const uriTest = "develop.m21-console.pages.dev";
const uriDev = "localhost:3000";

// Hosts considered development (decides which Hasura URL to target)
const DEV_HOSTS = new Set<string>([
  "localhost:3000",
  "develop.m21-console.pages.dev",
]);

// Default URLs (can be overridden via env vars)
const DEFAULT_DEV_URL =
  "https://console.gql.m21-ng-core.develop.telinhk.xyz/mobile/v1/graphql";
const DEFAULT_PROD_URL =
  "https://console.gql.m21-ng-core.live.telinhk.xyz/mobile/v1/graphql";

// CRA exposes only REACT_APP_* prefixed vars
const HASURA_DEV_URL = process.env.REACT_APP_HASURA_DEV_URL || DEFAULT_DEV_URL;
const HASURA_PROD_URL =
  process.env.REACT_APP_HASURA_PROD_URL || DEFAULT_PROD_URL;

export const resolveHasuraUrl = (): string => {
  try {
    if (typeof window !== "undefined" && DEV_HOSTS.has(window.location.host)) {
      return HASURA_DEV_URL;
    }
  } catch (e) {
    // No-op: window not available (SSR / tests) -> use prod as safe default
  }
  return HASURA_PROD_URL;
};

export class GraphqlError extends Error {
  public status?: number;
  public graphQLErrors?: any[];
  constructor(
    message: string,
    opts: { status?: number; graphQLErrors?: any[] } = {}
  ) {
    super(message);
    this.name = "GraphqlError";
    this.status = opts.status;
    this.graphQLErrors = opts.graphQLErrors;
  }
}

export const AuthorizationHeader = "Authorization";

export async function graphqlFetch<T = any, V = Record<string, any>>({
  query,
  variables,
  headers = {},
  token,
}: GraphqlRequestOptions<V>): Promise<T> {
  const url = resolveHasuraUrl();
  const queryString = typeof query === "string" ? query : print(query);

  // Acquire token (explicit > localStorage)
  let bearer: string | null | undefined = token;
  if (bearer === undefined) {
    try {
      bearer = localStorage.getItem("MBaaS.auth.accessToken");
    } catch (_) {
      bearer = null; // In non-browser contexts (tests)
    }
  }

  const finalHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };

  if (bearer) {
    finalHeaders[AuthorizationHeader] = `Bearer ${bearer}`;
  }

  const response = await fetch(url, {
    method: "POST",
    headers: finalHeaders,
    body: JSON.stringify({ query: queryString, variables }),
  });

  if (!response.ok) {
    throw new GraphqlError(
      `GraphQL network error: ${response.status} ${response.statusText}`,
      { status: response.status }
    );
  }

  let payload: any;
  try {
    payload = await response.json();
  } catch (e: any) {
    throw new GraphqlError(`Invalid JSON response: ${e.message}`);
  }

  if (payload.errors?.length) {
    const message =
      payload.errors
        .map((err: any) => err.message)
        .filter(Boolean)
        .join("; ") || "Unknown GraphQL error";
    throw new GraphqlError(message, {
      status: response.status,
      graphQLErrors: payload.errors,
    });
  }

  return payload.data as T;
}

// Convenience typed helper for queries (semantic sugar)
export const graphqlQuery = graphqlFetch;
export const graphqlMutation = graphqlFetch;

export default graphqlFetch;
